import React, { useEffect, useMemo, useState } from 'react';
import { PlusIcon } from '@heroicons/react/solid';
import { useNavigate } from 'react-router-dom';
import { Switch } from '@headlessui/react';
import { useForm } from 'react-hook-form';

import BankAccountModalContent from './BankAccounts/ActionModalContent';
import { showErrorToast, showSuccessToast } from 'components/Toast';
import InternalHeader from 'components/Navigation/InternalHeader';
import PrivateRoute from 'components/Navigation/ProtectedRoute';
import BaseInput from 'components/Inputs/BaseInput';
import SolidButton from 'components/SolidButton';
import Modal from 'components/Modal';

import { BANK_ACCOUNT_KINDS, BUTTON_COLORS, OPERATION_KINDS_AUX } from 'utils/enums';
import { DOC_VALIDATION, ERROR_MESSAGES, REQUIRED_RULE } from 'utils/constants';
import { IBank, IBankAccount, IOperation } from 'utils/interfaces';
import { letterOnly, numberOnly } from 'utils/inputValidation';
import { classNames, cleanObject } from 'utils/functions';
import StorageService from 'utils/storage';
import GeneralApi from 'utils/generalApi';
import { useAuth } from 'contexts/Auth';

export default function CreateOperationPage() {
  const {
    register,
    handleSubmit,
    watch,
    control,
    getValues,
    setValue,
    formState: { errors },
  } = useForm<IOperation>({
    defaultValues: {
      action: OPERATION_KINDS_AUX.C,
      origin_bank: 'BCP',
      pay_mode: 'RC',
      destiny_bank: 'BCP',
      account_type: BANK_ACCOUNT_KINDS.C,
      to_acc_number: '',
      to_number: '',
      to_doc: 'DNI',
    },
  });
  const history = useNavigate();
  const auth = useAuth();

  const userLevel = StorageService.get('userData')?.level;
  const generalApi = useMemo(() => new GeneralApi(auth, history), [auth, history]);

  const action = watch('action', OPERATION_KINDS_AUX.C);
  const payMode = watch('pay_mode', 'RC');
  const originBank = watch('origin_bank', 'BCP');
  const destinyBank = watch('destiny_bank', 'BCP');
  const accountType = watch('account_type', BANK_ACCOUNT_KINDS.C);
  const toDoc = watch('to_doc', 'DNI');

  const [currentBid, setCurrentBid] = useState(0);
  const [currentOffer, setCurrentOffer] = useState(0);
  const [loading, setLoading] = useState(false);
  const [banks, setBanks] = useState<Array<IBank>>([]);
  const [allBankAccounts, setAllBankAccounts] = useState<Array<IBankAccount>>([]);
  const [bankAccounts, setBankAccounts] = useState<Array<any>>([]);
  const [isThirdParty, setIsThirdParty] = useState(false);

  const [isActionOpen, setIsActionOpen] = useState(false);
  const [refreshBankAccounts, setRefreshBankAccounts] = useState(true);

  const isInterbank = useMemo(
    () =>
      banks.some(
        (obj) =>
          ([destinyBank, originBank].includes(obj.code) && obj.is_interbank) ||
          (destinyBank === obj.code && !obj.is_active)
      ),
    [banks, destinyBank, originBank]
  );

  const submitAction = async (data: IOperation) => {
    const finalData = {
      ...data,
      to_acc_number: data.to_acc_card,
      to_account: data.pay_mode === 'PC' || !isThirdParty ? 'P' : 'T',
      is_origin_interbank: banks.some((obj) => originBank === obj.code && obj.is_interbank),
    };
    if (payMode === 'RC' && isThirdParty) {
      finalData.to_acc_number = data.to_acc_number;
      if (isInterbank) finalData.to_cci = data.to_cci;
    } else if (payMode === 'RC') {
      finalData.to_acc_number = data.to_acc_number.split('|')[0];
      if (isInterbank) finalData.to_cci = data.to_acc_number.split('|')[1];
      finalData.to_doc = StorageService.get('userData')?.document_type;
      finalData.to_number = StorageService.get('userData')?.number;
      finalData.to_name = StorageService.get('userData')?.full_name;
    } else if (payMode === 'PC') {
      finalData.to_doc = StorageService.get('userData')?.document_type;
      finalData.to_number = StorageService.get('userData')?.number;
      finalData.to_name = StorageService.get('userData')?.full_name;
    }
    setLoading(true);
    let result: any = await generalApi.post(
      userLevel === '30' ? '/add_operation' : '/add_company_operation',
      cleanObject(finalData)
    );
    if (!result.success) {
      setLoading(false);
      return showErrorToast(result.data.err);
    }
    showSuccessToast('Operación creada');
    setTimeout(function () {
      history(`/operation/${result.data.operationId}`);
    }, 2000);
  };

  useEffect(() => {
    const getBankAccounts = async () => {
      const result = await generalApi.get(
        userLevel === '30' ? '/bank_accounts' : '/company_bank_accounts'
      );
      if (!result.success) {
        showErrorToast(result.message);
        return;
      }
      if (result.data) {
        setAllBankAccounts(result.data.bank_accounts);
      }
    };
    const getBanks = async () => {
      const result = await generalApi.get('/active_banks');
      if (!result.success) {
        showErrorToast(result.message);
        return;
      }
      if (result.data) {
        setBanks(result.data);
      }
    };
    const getSpecialService = async () => {
      const result = await generalApi.get('/special_service');
      if (!result.success) {
        showErrorToast(result.message);
        return;
      }
      return result.data.special_service;
    };
    const getCurrentChange = async () => {
      const result = await generalApi.get('/current_change');
      if (!result.success) {
        showErrorToast(result.message);
        return;
      }
      setCurrentBid(result.data.BID);
      setCurrentOffer(result.data.OFFER);
      const specialService = await getSpecialService();
      if (specialService) applySpecialService(result.data, specialService);
    };
    const applySpecialService = async (currentChange: any, specialService: any) => {
      const result = await generalApi.post('/get_bid_and_offer', {
        base: {
          change: currentChange.change,
          sp_compra: currentChange.sp_compra,
          sp_venta: currentChange.sp_venta,
        },
        ss: {
          sp_compra: specialService.sp_compra,
          sp_venta: specialService.sp_venta,
        },
      });
      if (!result.success) {
        showErrorToast(result.message);
        return;
      }
      setCurrentBid(result.data.BID);
      setCurrentOffer(result.data.OFFER);
    };
    if (refreshBankAccounts) getBankAccounts();
    getBanks();
    getCurrentChange();
  }, [generalApi, refreshBankAccounts, userLevel]);

  useEffect(() => {
    const finalBankAccounts = allBankAccounts
      ?.filter(
        (obj: IBankAccount) =>
          obj.code === destinyBank &&
          obj.account_type === accountType &&
          ((action === OPERATION_KINDS_AUX.C && obj.currency === 'USD') ||
            (action === OPERATION_KINDS_AUX.V && obj.currency === 'PEN'))
      )
      ?.map((obj: IBankAccount) => ({
        value: `${obj.number}|${obj.cci || ''}`,
        name: `${
          obj.description && obj.description.trim() !== ''
            ? obj.description
            : obj.number + ' - ' + obj.currency
        }`,
      }));
    setBankAccounts(finalBankAccounts);
    setValue('to_acc_number', finalBankAccounts.length > 0 ? finalBankAccounts[0].value : '');
  }, [allBankAccounts, destinyBank, accountType, action, setValue]);

  useEffect(() => {
    if (isThirdParty) setValue('to_acc_number', '');
    else setValue('to_acc_number', bankAccounts.length > 0 ? bankAccounts[0].value : '');
  }, [isThirdParty, bankAccounts, setValue]);

  useEffect(() => {
    DOC_VALIDATION[toDoc as keyof typeof DOC_VALIDATION].validate(
      getValues('to_number'),
      setValue,
      'to_number'
    );
  }, [toDoc, getValues, setValue]);

  const buildBaseInput = (
    label: string,
    type: React.HTMLInputTypeAttribute,
    name: string,
    rules: any,
    props: any
  ) => {
    return (
      <BaseInput
        label={label}
        type={type}
        name={name}
        register={register}
        errors={errors}
        watch={watch}
        control={control}
        rules={rules}
        {...props}
      />
    );
  };
  return (
    <PrivateRoute>
      <div className="min-h-full bg-gray-100 text-[#1B205A]">
        <Modal isOpen={isActionOpen} onClose={() => setIsActionOpen(false)}>
          <BankAccountModalContent
            title="Crear cuenta bancaria"
            defaultValues={{
              code: destinyBank,
              account_type: accountType,
              currency: action === OPERATION_KINDS_AUX.C ? 'USD' : 'PEN',
            }}
            onClose={() => setIsActionOpen(false)}
            onSuccess={() => {
              setRefreshBankAccounts(true);
              setIsActionOpen(false);
            }}
          />
        </Modal>
        <InternalHeader />
        <div className="mx-auto max-w-4xl pb-8 pt-32 sm:pt-0 2xl:px-24">
          <div className="justify-between space-y-4 px-8 pt-8 pb-4 sm:flex sm:space-y-0 sm:px-0">
            <p className="text-2xl font-bold">Crear Operación</p>
          </div>
          <form onSubmit={handleSubmit(submitAction)} className="bg-white shadow sm:rounded-lg">
            <div className="divide-y divide-gray-200">
              <div className="space-y-4 px-12 pt-4 pb-8">
                <p className="mb-4 text-center text-2xl font-bold">
                  Tipo de cambio: S/ {action === OPERATION_KINDS_AUX.C ? currentOffer : currentBid}
                </p>
                {buildBaseInput('Tipo de Operación', 'select', 'action', REQUIRED_RULE, {
                  options: [
                    { value: OPERATION_KINDS_AUX.C, name: 'Comprar dólares' },
                    { value: OPERATION_KINDS_AUX.V, name: 'Vender dólares' },
                  ],
                })}
                {buildBaseInput('Transfiero desde', 'select', 'origin_bank', REQUIRED_RULE, {
                  options: banks.map((bank: IBank) => ({ value: bank.code, name: bank.name })),
                })}
                {userLevel === '30' &&
                  buildBaseInput('Modo de Pago', 'select', 'pay_mode', REQUIRED_RULE, {
                    options: [
                      { value: 'RC', name: 'Recibirlos en mi cuenta' },
                      { value: 'PC', name: 'Pagar mi tarjeta de crédito' },
                    ],
                  })}
                {payMode === 'RC' && (
                  <>
                    {buildBaseInput(
                      `Deseo recibir los ${
                        action === OPERATION_KINDS_AUX.C ? 'DOLARES' : 'SOLES'
                      } en`,
                      'select',
                      'destiny_bank',
                      REQUIRED_RULE,
                      {
                        options: banks.map((bank: IBank) => ({
                          value: bank.code,
                          name: bank.name,
                        })),
                      }
                    )}
                    {buildBaseInput('Tipo de Cuenta', 'select', 'account_type', REQUIRED_RULE, {
                      options: [
                        { value: BANK_ACCOUNT_KINDS.A, name: 'Ahorros' },
                        { value: BANK_ACCOUNT_KINDS.C, name: 'Corriente' },
                      ],
                    })}
                    <Switch.Group as="div" className="flex items-center justify-center">
                      <Switch.Label as="span" className="mr-3">
                        <span className="text-sm font-medium text-gray-900">Propia</span>
                      </Switch.Label>
                      <Switch
                        checked={isThirdParty}
                        onChange={setIsThirdParty}
                        className={classNames(
                          'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent bg-primary-600 transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2'
                        )}
                      >
                        <span
                          aria-hidden="true"
                          className={classNames(
                            isThirdParty ? 'translate-x-5' : 'translate-x-0',
                            'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out'
                          )}
                        />
                      </Switch>
                      <Switch.Label as="span" className="ml-3">
                        <span className="text-sm font-medium text-gray-900">A un tercero</span>
                      </Switch.Label>
                    </Switch.Group>

                    {isThirdParty && (
                      <>
                        {buildBaseInput(
                          'Tipo de documento del Tercero',
                          'select',
                          'to_doc',
                          REQUIRED_RULE,
                          {
                            options: [
                              { value: 'DNI', name: 'DNI' },
                              { value: 'CE', name: 'Carné de extranjería' },
                              { value: 'PSP', name: 'Pasaporte' },
                              { value: 'DM', name: 'Doc. Militar' },
                              { value: 'RUC', name: 'RUC' },
                            ],
                          }
                        )}
                        {buildBaseInput(
                          'N° de documento del Tercero',
                          'text',
                          'to_number',
                          REQUIRED_RULE,
                          {
                            onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
                              DOC_VALIDATION[toDoc as keyof typeof DOC_VALIDATION].validate(
                                e.target.value,
                                setValue,
                                'to_number'
                              ),
                          }
                        )}
                        {buildBaseInput(
                          'Nombres y Apellidos del Tercero',
                          'text',
                          'to_name',
                          REQUIRED_RULE,
                          {
                            onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
                              setValue('to_name', letterOnly(e.target.value)),
                          }
                        )}
                        {buildBaseInput(
                          'Número de Cuenta del Tercero',
                          'text',
                          'to_acc_number',
                          {
                            ...REQUIRED_RULE,
                            minLength: {
                              value: 10,
                              message: ERROR_MESSAGES.MIN_LENGTH(10),
                            },
                            maxLength: {
                              value: 20,
                              message: ERROR_MESSAGES.MAX_LENGTH(20),
                            },
                          },
                          {
                            onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
                              setValue('to_acc_number', numberOnly(e.target.value)),
                          }
                        )}
                        {buildBaseInput(
                          `Código CCI del Tercero ${isInterbank ? '' : '(Opcional)'}`,
                          'text',
                          'to_cci',
                          {
                            required: isInterbank ? REQUIRED_RULE.required : false,
                            minLength: {
                              value: 20,
                              message: ERROR_MESSAGES.LENGTH(20),
                            },
                            maxLength: {
                              value: 20,
                              message: ERROR_MESSAGES.LENGTH(20),
                            },
                          },
                          {
                            onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
                              setValue('to_cci', numberOnly(e.target.value)),
                          }
                        )}
                      </>
                    )}

                    {!isThirdParty &&
                      buildBaseInput(
                        'Número de Cuenta Destino',
                        'select',
                        'to_acc_number',
                        REQUIRED_RULE,
                        {
                          options: bankAccounts,
                          buttonContent: (
                            <div className="flex items-center">
                              <PlusIcon className="mr-1 h-4 w-4" />
                              Crear
                            </div>
                          ),
                          buttonAction: () => setIsActionOpen(true),
                        }
                      )}
                  </>
                )}

                {payMode === 'PC' && (
                  <>
                    {buildBaseInput(
                      'Tipo de Tarjeta de Crédito',
                      'select',
                      'to_card',
                      REQUIRED_RULE,
                      {
                        options: [
                          { value: 'V', name: 'Visa' },
                          { value: 'DC', name: 'Diners Club' },
                          { value: 'AE', name: 'American Express' },
                          { value: 'MC', name: 'MasterCard' },
                        ],
                      }
                    )}
                    {buildBaseInput(
                      'Número de Tarjeta de Crédito',
                      'text',
                      'to_acc_card',
                      {
                        ...REQUIRED_RULE,
                        minLength: {
                          value: 15,
                          message: ERROR_MESSAGES.MIN_LENGTH(15),
                        },
                        maxLength: {
                          value: 16,
                          message: ERROR_MESSAGES.MAX_LENGTH(16),
                        },
                      },
                      {
                        onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
                          setValue('to_acc_card', numberOnly(e.target.value)),
                      }
                    )}
                    {buildBaseInput(
                      'Banco Emisor de la Tarjeta de Crédito',
                      'select',
                      'destiny_bank',
                      REQUIRED_RULE,
                      {
                        options: banks.map((bank: IBank) => ({
                          value: bank.code,
                          name: bank.name,
                        })),
                      }
                    )}
                  </>
                )}
              </div>
              <div className="space-y-4 px-8 pt-4 pb-8">
                <h4 className="font-bold">Recuerda:</h4>
                <span>- Verificar tu </span>
                <strong>límite de transferencia </strong>
                <span>con tu Banco</span>
                <br />
                <span>- Verificar si tu banco cobra </span>
                <strong>comisión interplaza</strong>
                <br />
                <span>- </span>
                <strong>NO </strong>
                <span>aceptamos depósitos en efectivo</span>
                <br />
              </div>
              {isInterbank && (
                <div className="space-y-4 px-8 pt-4 pb-8">
                  <span className="font-bold">
                    Si nos depositas con transferencia interbancaria inmediata
                  </span>
                  <br />
                  <span>- Antes de </span>
                  <strong>1:00pm </strong>
                  <span>en día hábil, recibes tu dinero el mismo día antes de </span>
                  <strong>6:00pm</strong>
                  <br />
                  <span>- Luego de </span>
                  <strong>1:00pm</strong>
                  <span>, recibes tu dinero al día hábil siguiente antes de </span>
                  <strong>6:00pm</strong>
                  <br />
                  <span className="font-bold">
                    Si nos depositas con transferencia interbancaria diferida
                  </span>
                  <br />
                  <span>- Recibirás tu dinero al día hábil siguiente antes de las </span>
                  <strong>6:00pm</strong>
                  <br />
                </div>
              )}
              {payMode === 'PC' && (
                <div className="space-y-4 px-8 pt-4 pb-8">
                  <span className="font-bold">Pagos en línea:</span>
                  <br />
                  <span>- Solo pago de Tarjeta de crédito BCP</span>
                  <br />
                  <span className="font-bold">Para Pagos de Tarjetas de otros bancos</span>
                  <br />
                  <span>- Antes de </span>
                  <strong>1:00pm </strong>
                  <span>en día hábil, se pagará el mismo día antes de </span>
                  <strong>6:00pm</strong>
                  <br />
                  <span>- Luego de </span>
                  <strong>1:00pm</strong>
                  <span>, se pagará al día hábil siguiente antes de </span>
                  <strong>6:00pm</strong>
                  <br />
                  <span className="font-bold">
                    Si nos depositas con transferencia interbancaria diferida
                  </span>
                  <br />
                  <span>- Se pagará al día hábil siguiente antes de las </span>
                  <strong>6:00pm</strong>
                </div>
              )}
            </div>
            <div className="flex justify-center bg-gray-50 py-4 px-8 sm:rounded-b-lg">
              <SolidButton color={BUTTON_COLORS.BLUE} className="w-auto px-12" isLoading={loading}>
                Aceptar
              </SolidButton>
            </div>
          </form>
        </div>
      </div>
    </PrivateRoute>
  );
}
